import type { CaptureFormat } from './captureEncoding'

/**
 * What a capture resolution and file format cost downstream.
 *
 * Every captured photo is encoded in the camera's Photo Format and pushed through the
 * upload queue, so the pixel count and format chosen in Camera Settings directly drive
 * filesize, upload time and local disk growth. The numbers here are deliberately coarse:
 * their job is to make the order of magnitude visible next to the pickers, not to be exact.
 */

export type CaptureImpactLevel = 'light' | 'moderate' | 'heavy'

export interface CaptureFootprint {
  /** Estimated size of a single capture, in MB. */
  megabytes: number
  /** Estimated seconds to upload a single capture over ASSUMED_UPLINK_MBPS. */
  uploadSeconds: number
  level: CaptureImpactLevel
}

/**
 * Venue uplink the upload estimate is quoted against. Deliberately conservative —
 * booths usually run on shared event Wi-Fi or a phone tether, not office fibre.
 */
export const ASSUMED_UPLINK_MBPS = 5

/** Observed JPEG bytes per pixel at a given encoder quality, for typical booth frames. */
const BYTES_PER_PIXEL_BY_QUALITY: ReadonlyArray<{ quality: number; bytesPerPixel: number }> = [
  { quality: 0.4, bytesPerPixel: 0.08 },
  { quality: 0.6, bytesPerPixel: 0.12 },
  { quality: 0.8, bytesPerPixel: 0.22 },
  { quality: 1, bytesPerPixel: 0.5 }
]

/**
 * PNG ignores the quality knob — it stores the earlier JPEG pass's output losslessly — so
 * one figure covers every preset. ≈3.2 MB at 1080p, in line with the ~3.3 MB per photo
 * booths on PNG reported.
 */
const PNG_BYTES_PER_PIXEL = 1.6

/** Files above this are large enough that a weak uplink visibly lags behind. */
const MODERATE_THRESHOLD_MB = 1.3
const HEAVY_THRESHOLD_MB = 2.2

function bytesPerPixelFor(quality: number): number {
  const first = BYTES_PER_PIXEL_BY_QUALITY[0]
  const last = BYTES_PER_PIXEL_BY_QUALITY[BYTES_PER_PIXEL_BY_QUALITY.length - 1]
  const clamped = Math.min(Math.max(quality, first.quality), last.quality)

  for (let i = 1; i < BYTES_PER_PIXEL_BY_QUALITY.length; i++) {
    const prev = BYTES_PER_PIXEL_BY_QUALITY[i - 1]
    const next = BYTES_PER_PIXEL_BY_QUALITY[i]
    if (clamped <= next.quality) {
      const span = next.quality - prev.quality
      const ratio = span === 0 ? 0 : (clamped - prev.quality) / span
      return prev.bytesPerPixel + ratio * (next.bytesPerPixel - prev.bytesPerPixel)
    }
  }

  return last.bytesPerPixel
}

function impactLevelFor(megabytes: number): CaptureImpactLevel {
  if (megabytes <= MODERATE_THRESHOLD_MB) return 'light'
  if (megabytes <= HEAVY_THRESHOLD_MB) return 'moderate'
  return 'heavy'
}

export function estimateCaptureFootprint(
  width: number,
  height: number,
  quality: number,
  format: CaptureFormat
): CaptureFootprint {
  const pixels = Math.max(width, 0) * Math.max(height, 0)
  const bytesPerPixel = format === 'png' ? PNG_BYTES_PER_PIXEL : bytesPerPixelFor(quality)
  const megabytes = (pixels * bytesPerPixel) / (1024 * 1024)
  const uploadSeconds = (megabytes * 8) / ASSUMED_UPLINK_MBPS

  return { megabytes, uploadSeconds, level: impactLevelFor(megabytes) }
}

/** Short display form — sub-megabyte captures need the extra decimal to read sensibly. */
export function formatMegabytes(megabytes: number): string {
  return megabytes < 1 ? megabytes.toFixed(2) : megabytes.toFixed(1)
}

export function formatUploadSeconds(uploadSeconds: number): string {
  return uploadSeconds < 1 ? '<1' : String(Math.round(uploadSeconds))
}
